import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, User, Bot, Headset, Loader2, Camera } from 'lucide-react';
import AIVisualGuide from './AIVisualGuide';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatWidget() {
    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem('voxoora_chat_session') || '');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [status, setStatus] = useState('ai');
    const [name, setName] = useState('');
    const [started, setStarted] = useState(false);
    const [unread, setUnread] = useState(0);
    const [visualGuideMode, setVisualGuideMode] = useState(false);
    const messagesEndRef = useRef(null);
    const pollRef = useRef(null);

    const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    useEffect(scrollToBottom, [messages]);

    const fetchMessages = useCallback(async (sid) => {
        if (!sid) return;
        try {
            const res = await axios.get(`${API}/api/chat/messages/${sid}`);
            setMessages(res.data.messages || []);
            setStatus(res.data.status || 'ai');
        } catch (e) {
            console.error('Failed to fetch messages:', e);
        }
    }, []);

    // Start session
    const startChat = async () => {
        if (!name.trim()) return;
        try {
            const res = await axios.post(`${API}/api/chat/session`, { name: name.trim(), session_id: sessionId || undefined });
            const sid = res.data.session_id;
            setSessionId(sid);
            setStatus(res.data.status);
            localStorage.setItem('voxoora_chat_session', sid);
            setStarted(true);
            await fetchMessages(sid);

            if (res.data.status === 'ai' && (!messages || messages.length === 0)) {
                // Show welcome message locally
                setMessages([{
                    id: 'welcome',
                    role: 'assistant',
                    text: `Hi ${name.trim()}! I'm Voxoora's AI assistant. How can I help you today?`,
                    created_at: new Date().toISOString()
                }]);
            }
        } catch (e) {
            console.error('Failed to start chat:', e);
        }
    };

    // Resume session on open
    useEffect(() => {
        if (open && sessionId) {
            setStarted(true);
            fetchMessages(sessionId);
        }
    }, [open, sessionId, fetchMessages]);

    // Poll for new messages when escalated
    useEffect(() => {
        if (!open || !sessionId || status !== 'escalated') {
            clearInterval(pollRef.current);
            return;
        }
        pollRef.current = setInterval(() => fetchMessages(sessionId), 4000);
        return () => clearInterval(pollRef.current);
    }, [open, sessionId, status, fetchMessages]);

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || sending) return;
        setInput('');
        setSending(true);

        // Optimistic UI
        const tempMsg = { id: 'temp-' + Date.now(), role: 'user', text, created_at: new Date().toISOString() };
        setMessages(prev => [...prev, tempMsg]);

        try {
            const res = await axios.post(`${API}/api/chat/message`, { session_id: sessionId, text });
            if (res.data.reply) {
                setMessages(prev => [...prev.filter(m => m.id !== tempMsg.id), tempMsg, {
                    id: 'ai-' + Date.now(),
                    role: res.data.status === 'escalated' ? 'assistant' : 'assistant',
                    text: res.data.reply,
                    created_at: new Date().toISOString()
                }]);
            }
            if (res.data.status) setStatus(res.data.status);
        } catch (e) {
            console.error('Failed to send message:', e);
        } finally {
            setSending(false);
        }
    };

    const escalate = async () => {
        try {
            await axios.post(`${API}/api/chat/escalate/${sessionId}`);
            setStatus('escalated');
            await fetchMessages(sessionId);
        } catch (e) {
            console.error('Escalation failed:', e);
        }
    };

    const roleIcon = (role) => {
        if (role === 'user') return <User className="w-4 h-4" />;
        if (role === 'admin') return <Headset className="w-4 h-4" />;
        if (role === 'system') return null;
        return <Bot className="w-4 h-4" />;
    };

    const roleBg = (role) => {
        if (role === 'user') return 'bg-gold/15 text-gold border-gold/20';
        if (role === 'admin') return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20';
        if (role === 'system') return 'bg-white/5 text-white/40 border-white/5 text-center text-xs italic';
        return 'bg-white/8 text-white/90 border-white/10';
    };

    return (
        <>
            {/* Floating Bubble */}
            {!open && (
                <button
                    data-testid="chat-bubble"
                    onClick={() => { setOpen(true); setUnread(0); }}
                    className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-50 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gold text-[#0A0D14] flex items-center justify-center shadow-lg shadow-gold/25 hover:scale-105 transition-transform"
                >
                    <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8" />
                    <Camera className="w-3.5 h-3.5 absolute bottom-1 right-1 bg-[#0A0D14] rounded-full p-0.5 text-gold" />
                    {unread > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unread}</span>}
                </button>
            )}

            {/* Chat Panel */}
            {open && (
                <div className="fixed bottom-6 right-6 z-50 w-[380px] h-[540px] flex flex-col bg-[#0D1017] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden" data-testid="chat-panel">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-[#121620] border-b border-white/10">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center">
                                <MessageCircle className="w-4 h-4 text-gold" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-white">Voxoora Support</p>
                                <p className="text-[10px] text-white/40">
                                    {status === 'ai' ? 'AI Assistant' : status === 'escalated' ? 'Live Agent' : 'Resolved'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setVisualGuideMode(!visualGuideMode)}
                                className={`p-1.5 rounded-lg hover:bg-white/5 transition-colors ${visualGuideMode ? 'text-gold bg-gold/10' : 'text-white/40 hover:text-white'}`}
                                data-testid="chat-visual-guide-toggle" title="AI Visual Guide">
                                <Camera className="w-4 h-4" />
                            </button>
                            <button onClick={() => { setOpen(false); setVisualGuideMode(false); }} className="p-1.5 rounded-lg hover:bg-white/5 text-white/40 hover:text-white transition-colors" data-testid="chat-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Visual Guide Mode */}
                    {visualGuideMode ? (
                        <AIVisualGuide language="en" onClose={() => setVisualGuideMode(false)} />
                    ) : (
                    <>
                    {/* Pre-chat Name Form */}
                    {!started ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4">
                            <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mb-2">
                                <MessageCircle className="w-8 h-8 text-gold" />
                            </div>
                            <h3 className="text-white font-semibold text-lg">Welcome to Voxoora</h3>
                            <p className="text-white/50 text-sm text-center">Our AI assistant is ready to help. Enter your name to start chatting.</p>
                            <input
                                data-testid="chat-name-input"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && startChat()}
                                placeholder="Your name"
                                className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/40"
                            />
                            <button
                                data-testid="chat-start-btn"
                                onClick={startChat}
                                disabled={!name.trim()}
                                className="w-full py-2.5 rounded-lg bg-gold text-[#0A0D14] font-semibold text-sm disabled:opacity-40 hover:bg-gold/90 transition-colors"
                            >
                                Start Chat
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
                                {messages.map(msg => (
                                    <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'}`}>
                                        <div className={`max-w-[85%] px-3 py-2 rounded-xl border text-[13px] leading-relaxed ${roleBg(msg.role)}`}>
                                            {msg.role !== 'system' && (
                                                <div className="flex items-center gap-1.5 mb-1 opacity-60">
                                                    {roleIcon(msg.role)}
                                                    <span className="text-[10px] font-medium">
                                                        {msg.role === 'user' ? 'You' : msg.role === 'admin' ? (msg.admin_name || 'Support') : 'AI Assistant'}
                                                    </span>
                                                </div>
                                            )}
                                            <p className="whitespace-pre-wrap">{msg.text}</p>
                                        </div>
                                    </div>
                                ))}
                                {sending && (
                                    <div className="flex justify-start">
                                        <div className="px-3 py-2 rounded-xl bg-white/5 border border-white/10">
                                            <Loader2 className="w-4 h-4 text-white/40 animate-spin" />
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Escalate Button */}
                            {status === 'ai' && messages.length > 2 && (
                                <div className="px-4 py-1">
                                    <button
                                        data-testid="chat-escalate-btn"
                                        onClick={escalate}
                                        className="w-full py-1.5 text-[11px] text-white/40 hover:text-gold/80 transition-colors flex items-center justify-center gap-1"
                                    >
                                        <Headset className="w-3 h-3" /> Talk to a human agent
                                    </button>
                                </div>
                            )}

                            {status === 'resolved' && (
                                <div className="px-4 py-2 text-center text-xs text-white/30">
                                    This conversation has been resolved. Start a new chat if you need more help.
                                </div>
                            )}

                            {/* Input */}
                            {status !== 'resolved' && (
                                <div className="px-3 py-3 border-t border-white/10 flex gap-2">
                                    <input
                                        data-testid="chat-input"
                                        value={input}
                                        onChange={e => setInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                                        placeholder={status === 'escalated' ? 'Message support team...' : 'Ask anything...'}
                                        disabled={sending}
                                        className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-gold/30"
                                    />
                                    <button
                                        data-testid="chat-send-btn"
                                        onClick={sendMessage}
                                        disabled={!input.trim() || sending}
                                        className="px-3 py-2 rounded-lg bg-gold text-[#0A0D14] disabled:opacity-30 hover:bg-gold/90 transition-colors"
                                    >
                                        <Send className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                    </>
                    )}
                </div>
            )}
        </>
    );
}
